import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export interface BrowserAuthConfiguration {
  supabaseUrl: string;
  supabaseAnonymousKey: string;
  redirectUrl: string;
}

export function createBrowserAuth(configuration: BrowserAuthConfiguration): SupabaseClient {
  return createClient(configuration.supabaseUrl, configuration.supabaseAnonymousKey, {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    },
  });
}

export async function signInWithEntra(client: SupabaseClient, redirectUrl: string): Promise<void> {
  const { error } = await client.auth.signInWithOAuth({
    provider: 'azure',
    options: { scopes: 'email', redirectTo: redirectUrl },
  });
  if (error) throw error;
}

export interface AuthCallbackResult {
  handled: boolean;
  cleanUrl?: string;
}

export async function completeEntraCallback(
  client: SupabaseClient,
  currentUrl: string,
): Promise<AuthCallbackResult> {
  const url = new URL(currentUrl);
  const isCallback = url.pathname === '/auth/callback';
  if (!isCallback) return { handled: false };

  const providerError = url.searchParams.has('error') || url.searchParams.has('error_description');
  if (providerError) throw new Error('Microsoft sign-in was not completed.');

  const code = url.searchParams.get('code');
  if (!code) throw new Error('Microsoft sign-in returned without a one-time authorization code.');

  const { error } = await client.auth.exchangeCodeForSession(code);
  if (error) throw new Error('Microsoft sign-in could not be verified.');

  return { handled: true, cleanUrl: `${url.origin}/` };
}
